import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { Apelacion, Sancion, Socio } from '../../models/types';
import { FaExclamationTriangle, FaCheck, FaTimes, FaEye, FaPlus } from 'react-icons/fa';
import { theme } from '../../theme';

// Estilos
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  border-bottom: 2px solid ${theme.colors.primary.main};
  padding-bottom: 15px;
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 2.2rem;
  font-weight: ${theme.typography.fontWeights.bold};
  
  svg {
    color: ${theme.colors.primary.main};
  }
`;

const Button = styled.button`
  background-color: #101325;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1rem;
  
  svg {
    margin-right: 8px;
  }
  
  &:hover {
    background-color: #1a1f3d;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  margin-bottom: 30px;
  
  th, td {
    padding: 15px;
    text-align: left;
    border-bottom: 1px solid #eee;
  }
  
  th {
    background-color: ${theme.colors.primary.main};
    font-weight: ${theme.typography.fontWeights.semibold};
    color: white;
  }
  
  tr:hover {
    background-color: ${theme.colors.background.default};
  }
  
  tr:last-child td {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ $status: 'pendiente' | 'aceptada' | 'rechazada' }>`
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: bold;
  background-color: ${props => {
    switch (props.$status) {
      case 'pendiente': return theme.colors.warning.main;
      case 'aceptada': return theme.colors.success.main;
      case 'rechazada': return theme.colors.secondary.main;
      default: return theme.colors.warning.main;
    }
  }};
  color: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
  }
`;

const ActionButton = styled.button`
  background-color: transparent;
  border: none;
  cursor: pointer;
  padding: 5px;
  margin-right: 5px;
  color: #101325;
  
  &:hover {
    color: #e4312e;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  color: ${theme.colors.text.secondary};
  
  svg {
    font-size: 3rem;
    color: ${theme.colors.primary.main};
    opacity: 0.7;
  }
  
  h3 {
    margin: 10px 0;
    color: ${theme.colors.text.primary};
  }
  
  p {
    margin: 0;
  }
`;

const ApelacionesList: React.FC = () => {
  const navigate = useNavigate();
  const { data, loadData } = useContext(AppContext);
  const [filtroEstado, setFiltroEstado] = useState<string>('todos');
  const [filtroSocio, setFiltroSocio] = useState<string>('todos');
  const [apelacionesFiltradas, setApelacionesFiltradas] = useState<Apelacion[]>([]);
  
  // Inicializar con datos de ejemplo si no hay apelaciones
  useEffect(() => {
    if (!data.apelaciones || data.apelaciones.length === 0) {
      // Aquí se podrían inicializar datos de ejemplo
      // o simplemente mostrar un estado vacío
    }
    
    aplicarFiltros();
  }, [data, filtroEstado, filtroSocio]);
  
  const aplicarFiltros = () => {
    let apelacionesFiltradas = data.apelaciones || [];
    
    if (filtroEstado !== 'todos') {
      apelacionesFiltradas = apelacionesFiltradas.filter(
        (apelacion: Apelacion) => apelacion.estado === filtroEstado
      );
    }
    
    if (filtroSocio !== 'todos') {
      apelacionesFiltradas = apelacionesFiltradas.filter(
        (apelacion: Apelacion) => apelacion.socioId === filtroSocio
      );
    }
    
    setApelacionesFiltradas(apelacionesFiltradas);
  };
  
  const getNombreSocio = (socioId: string) => {
    const socio = data.socios.find((s: Socio) => s.id === socioId);
    return socio ? `${socio.nombre} ${socio.apellido}` : 'Piloto desconocido';
  };
  
  const getDescripcionSancion = (sancionId: string) => {
    const sancion = data.sanciones.find((s: Sancion) => s.id === sancionId);
    return sancion ? sancion.descripcion : 'Sanción desconocida';
  };
  
  return (
    <Container>
      <Header>
        <Title><FaExclamationTriangle /> Sistema de Apelaciones</Title>
        <Button onClick={() => navigate('/apelaciones/new')}>
          <FaPlus /> Nueva Apelación
        </Button>
      </Header>
      
      <FilterContainer>
        <div>
          <label>Estado: </label>
          <FilterSelect 
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
          >
            <option value="todos">Todos</option>
            <option value="pendiente">Pendientes</option>
            <option value="aceptada">Aceptadas</option>
            <option value="rechazada">Rechazadas</option>
          </FilterSelect>
        </div>
        
        <div>
          <label>Piloto: </label>
          <FilterSelect 
            value={filtroSocio}
            onChange={(e) => setFiltroSocio(e.target.value)}
          >
            <option value="todos">Todos</option>
            {data.socios.map((socio: Socio) => (
              <option key={socio.id} value={socio.id}>
                {socio.nombre} {socio.apellido}
              </option>
            ))}
          </FilterSelect>
        </div>
      </FilterContainer>
      
      {apelacionesFiltradas.length > 0 ? (
        <Table>
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Piloto</th>
              <th>Sanción</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {apelacionesFiltradas.map(apelacion => (
              <tr key={apelacion.id}>
                <td>{apelacion.fechaApelacion}</td>
                <td>{getNombreSocio(apelacion.socioId)}</td>
                <td>{getDescripcionSancion(apelacion.sancionId)}</td>
                <td>
                  <StatusBadge $status={apelacion.estado}>
                    {apelacion.estado === 'pendiente' && 'Pendiente'}
                    {apelacion.estado === 'aceptada' && 'Aceptada'}
                    {apelacion.estado === 'rechazada' && 'Rechazada'}
                  </StatusBadge>
                </td>
                <td>
                  <ActionButton onClick={() => navigate(`/apelaciones/${apelacion.id}`)}>
                    <FaEye size={18} title="Ver detalles" />
                  </ActionButton>
                  {apelacion.estado === 'pendiente' && (
                    <>
                      <ActionButton onClick={() => navigate(`/apelaciones/${apelacion.id}/resolver?accion=aceptar`)}>
                        <FaCheck size={18} title="Aceptar apelación" />
                      </ActionButton>
                      <ActionButton onClick={() => navigate(`/apelaciones/${apelacion.id}/resolver?accion=rechazar`)}>
                        <FaTimes size={18} title="Rechazar apelación" />
                      </ActionButton>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      ) : (
        <EmptyState>
          <FaExclamationTriangle size={40} style={{ marginBottom: '15px' }} />
          <h3>No hay apelaciones que coincidan con los filtros seleccionados</h3>
          <p>Intenta cambiar los filtros o crea una nueva apelación</p>
        </EmptyState>
      )}
    </Container>
  );
};

export default ApelacionesList;
